import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { QueryKey, UseQueryOptions } from 'react-query';
import http from '../config/http-service';
import type { GetRestaurantInquiryParams, RestaurantInquiry } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// GET api/v1/cms/restaurant-inquiries/:id — one inquiry with everything the
// detail page needs (header, restaurant, assignee, full item list). 404s if the
// inquiry doesn't exist or is soft-deleted. Pass { includeNewMatches: true } to
// also get newMatches[] on every item (active-deal matches, read-time-computed).
type TData = RestaurantInquiry;
type TError = AxiosError;
type Options = Omit<UseQueryOptions<TData, TError, TData, QueryKey>, 'queryKey' | 'queryFn'>;

export function useGetRestaurantInquiryApi(
  id: number | string | undefined,
  params: GetRestaurantInquiryParams = {},
  options?: Options
) {
  // Drop undefined/null so the bare call keeps hitting the exact same URL
  // (and query key) it always has.
  const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null));

  return useQueryWrapper<TData, TError>(
    [restaurantInquiriesEndpoint, 'detail', id, cleaned],
    async () => {
      const response = await http.get(`${restaurantInquiriesEndpoint}/${id}`, { params: cleaned });
      // The API wraps the resource in an envelope: { data: {...} }.
      return response.data?.data ?? response.data;
    },
    { enabled: id !== undefined && id !== null && id !== '', ...options }
  );
}
